import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createEditor, Descendant, Transforms } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, ReactEditor, RenderElementProps, RenderLeafProps, Slate, withReact } from 'slate-react';
import isHotkey from 'is-hotkey';

import Toolbar from './Toolbar.web';
import { EditorConfigContext, useEditorConfig, AlignType, isAlignElement, isEditorEmpty, slateToRTF, toggleMark } from './config';
import { rtfToSlate } from './helpers';
import { CustomTextKey } from './types';

import ColorWrapper from '@styles/colors';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import type { RichTextEditorWebProps } from './types';

const HOTKEYS: [string, CustomTextKey][] = [
  ['mod+b', 'bold'],
  ['mod+i', 'italic'],
  ['mod+u', 'underline'],
  ['mod+s', 'strikethrough'],
];

const Element = ({ attributes, children, element }: RenderElementProps) => {
  const style: React.CSSProperties = {
    lineHeight: 1.6,
  };
  if (isAlignElement(element)) {
    style.textAlign = element.align as AlignType;
  }

  switch (element.type) {
    case 'bulleted-list':
      return (
        <ul style={style} {...attributes}>
          {children}
        </ul>
      );
    case 'list-item':
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      );
    case 'numbered-list':
      return (
        <ol style={style} {...attributes}>
          {children}
        </ol>
      );
    default:
      const defaultStyles = {
        margin: 0,
        padding: 0,
      };

      return (
        <p style={Object.assign(defaultStyles, style)} {...attributes}>
          {children}
        </p>
      );
  }
};

const Leaf = ({ attributes, children, leaf }: RenderLeafProps) => {
  const { scale } = useEditorConfig();

  if (leaf.bold) {
    children = <strong style={{ fontWeight: 700 }}>{children}</strong>;
  }

  if (leaf.italic) {
    children = <em>{children}</em>;
  }

  if (leaf.underline) {
    children = <u>{children}</u>;
  }

  if (leaf.strikethrough) {
    children = <span style={{ textDecoration: 'line-through' }}>{children}</span>;
  }

  if (leaf.color) {
    children = <span style={{ color: leaf.color }}>{children}</span>;
  }

  if (leaf.fontSize) {
    children = <span style={{ fontSize: `${scale(leaf.fontSize)}px` }}>{children}</span>;
  }

  return <span {...attributes}>{children}</span>;
};

const Editor = ({
  C,
  onChange,
  generateImageURL,
  setTouched,
  readOnly = false,
  previewNote,
  font = initialFont,
  scale = initialScale,
  activeNote = null,
  rawContent,
  editorBackgroundColor,
  editorBorderColor,
}: RichTextEditorWebProps) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [initialEditorValue, setInitialEditorValue] = useState<Descendant[]>([]);
  const [noteId, setNoteId] = useState<number | null>(null);

  const editor = useMemo(() => withHistory(withReact(createEditor())), []);
  const editableRef = useRef<HTMLDivElement>(null);

  const renderElement = useCallback((props: RenderElementProps) => <Element {...props} />, []);
  const renderLeaf = useCallback((props: RenderLeafProps) => <Leaf {...props} />, []);

  const focusOnEditor = useCallback(() => {
    const editorNode = editableRef.current;

    if (!editorNode) {
      return;
    }

    // Focus the editor while maintaining cursor position or selection
    // ReactEditor.focus handles both cursor position and text selection
    ReactEditor.focus(editor);
  }, [editor]);

  useEffect(() => {
    if (!activeNote && !previewNote) {
      if (rawContent) {
        const slateContent = rtfToSlate(rawContent);
        setInitialEditorValue(slateContent);
      } else {
        setInitialEditorValue([{ type: 'paragraph', children: [{ text: '' }] }]);
      }
      setLoading(false);
      setNoteId(null);
    } else {
      const item = previewNote ?? activeNote;
      const itemId = item?.documentId || item?.id;

      // Check if this is the same note and we have content
      if (itemId === noteId && initialEditorValue.length > 0 && !item?.rawContent && !rawContent) {
        return;
      }

      // Check for rawContent in order: prop, activeNote, previewNote
      const contentToUse = rawContent || item?.rawContent;

      if (contentToUse) {
        const slateContent = rtfToSlate(contentToUse);
        setInitialEditorValue(slateContent);
        setNoteId(itemId);
        setLoading(false);
      } else {
        // Otherwise, fetch from server
        setLoading(true);

        (async () => {
          try {
            const url = await generateImageURL({ item: { ...item, imageHash: item.contentHash }, download: true });
            const response = await fetch(url, { ignoreInterceptor: 'true' });
            const fileContent = await response.text();

            const slateContent = rtfToSlate(fileContent);
            setInitialEditorValue(slateContent);

            setNoteId(itemId);
            setLoading(false);
          } catch (error) {
            console.error('Error loading content:', error);
            setLoading(false);
          }
        })();
      }
    }
  }, [activeNote, previewNote, rawContent]);

  const onEditorChange = (value: Descendant[]) => {
    if (isEditorEmpty(value) && !activeNote) {
      setTouched(false);
      onChange('');
      return;
    }

    const rtfContent = slateToRTF(value);
    setTouched(true);
    onChange(rtfContent);
  };

  if (loading || initialEditorValue.length === 0) {
    return null;
  }

  return (
    <EditorConfigContext.Provider value={{ scale, font }}>
      <Slate editor={editor} onChange={onEditorChange} initialValue={initialEditorValue}>
        {!readOnly ? <Toolbar focus={focusOnEditor} /> : null}
        <Editable
          readOnly={readOnly}
          ref={editableRef}
          style={{
            padding: scale(18),
            paddingTop: readOnly ? 0 : scale(18),
            backgroundColor: editorBackgroundColor ?? C('Background/Light/Field on Panel [mobile]'),
            borderRadius: scale(SCREEN_BORDER_RADIUS),
            ...(editorBorderColor ? { border: `${scale(1)}px solid ${editorBorderColor}` } : {}),
            ...font['Body Large'],
            lineHeight: scale(1.6),
            height: '100%',
            overflowY: 'scroll',
            color: C('Label/Light/Primary'),
            outline: 'none',
          }}
          contentEditable
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          spellCheck
          onKeyDown={(event) => {
            for (const [hotkey, mark] of HOTKEYS) {
              if (isHotkey(hotkey, event)) {
                event.preventDefault();

                toggleMark(editor, mark);
                focusOnEditor();
              }
            }
          }}
        />
      </Slate>
    </EditorConfigContext.Provider>
  );
};

export default ColorWrapper(Editor);
